
import Vue from 'vue';
import { mapState } from 'vuex';
import { hundredthsToTokens, TOKEN_COOLDOWN_TIME_BUCKET, formatCooldownTimer, getCooldownTimeBucketShort } from '@run-diffusion/shared';
import moment from 'moment-timezone';
import { RUNNITS_OWNER_SELECTION } from './constants';
import MenuOrBottomSheet from '@/components/base/MenuOrBottomSheet.vue';
import TokensSVG from '@/assets/TokensSVG.vue';

export default Vue.extend({
	name: 'CooldownProgressMenu',
	components: {
		MenuOrBottomSheet,
		TokensSVG,
	},
	props: {
		/** Current run cost in hundredths; menu shows warning when this exceeds cooldown threshold (not remaining budget). */
		currentRunCost: { type: Number, default: null },
	},
	data () {
		return {
			menuOpen: false,
		};
	},
	computed: {
		...mapState(['runnitState', 'realTimeMoment']),
		cooldownData () {
			if (this.runnitState.runnitsOwnerSelection !== RUNNITS_OWNER_SELECTION.TEAM) return null;
			return this.runnitState.tokenCooldownData?.cooldownData ?? null;
		},
		/** True when current run cost exceeds cooldown threshold (limit per bucket), not remaining budget. */
		runCostExceedsThreshold (): boolean {
			const cost = this.currentRunCost;
			const d = this.cooldownData;
			if (cost == null || typeof cost !== 'number' || !d || typeof d.threshold !== 'number') return false;
			return cost > d.threshold;
		},
		showCooldownProgressCircle (): boolean {
			const d = this.cooldownData;
			if (!d || typeof d.threshold !== 'number' || d.threshold <= 0) return false;
			const ratio = d.tokensSpent / d.threshold;
			if (ratio <= 0.5) return false;
			const endsAt = this.runnitState.tokenCooldownData?.cooldownEndsAt;
			if (!endsAt || !this.realTimeMoment) return true;
			return moment(endsAt).isAfter(this.realTimeMoment);
		},
		cooldownProgressRatio (): number {
			const d = this.cooldownData;
			if (!d || typeof d.threshold !== 'number' || d.threshold <= 0) return 0;
			return Math.min(1, d.tokensSpent / d.threshold);
		},
		cooldownProgressPercent (): number {
			return Math.round(this.cooldownProgressRatio * 100);
		},
		cooldownProgressRingColor (): string {
			const r = this.cooldownProgressRatio;
			if (r >= 0.8) return 'var(--red-400, #f87171)';
			return 'var(--yellow-400, #facc15)';
		},
		/** Timer format: "4:13 / 5m" or "1:27:00 / 2h" (shared util). */
		cooldownResetTimeText (): string {
			const endsAt = this.runnitState.tokenCooldownData?.cooldownEndsAt;
			if (!endsAt || !this.realTimeMoment) return '—';
			const resetAtMoment = moment(endsAt);
			const totalSeconds = Math.max(0, resetAtMoment.diff(this.realTimeMoment, 'seconds'));
			if (totalSeconds <= 0) return 'ready';
			const bucketShort = getCooldownTimeBucketShort(this.cooldownData?.timeBucket);
			return formatCooldownTimer(totalSeconds, bucketShort);
		},
		cooldownTimeBucketShort (): string {
			return getCooldownTimeBucketShort(this.cooldownData?.timeBucket);
		},
		cooldownSpentDisplay (): string {
			const d = this.cooldownData;
			if (!d || typeof d.tokensSpent !== 'number') return '—';
			return String(hundredthsToTokens(d.tokensSpent));
		},
		cooldownThresholdDisplay (): string {
			const d = this.cooldownData;
			if (!d || typeof d.threshold !== 'number') return '—';
			return String(hundredthsToTokens(d.threshold));
		},
		/** Compact form for chip: 10000 → "10k", 1500 → "1.5k", 999 → "999". */
		cooldownSpentDisplayCompact (): string {
			const d = this.cooldownData;
			if (!d || typeof d.tokensSpent !== 'number') return '—';
			return this.formatCompactTokenCount(hundredthsToTokens(d.tokensSpent));
		},
		/** Compact form for chip and description when large. */
		cooldownThresholdDisplayCompact (): string {
			const d = this.cooldownData;
			if (!d || typeof d.threshold !== 'number') return '—';
			return this.formatCompactTokenCount(hundredthsToTokens(d.threshold));
		},
		cooldownTimeBucketLabel (): string {
			const timeBucket = this.cooldownData?.timeBucket;
			if (!timeBucket) return '—';
			const map: Record<string, string> = {
				[TOKEN_COOLDOWN_TIME_BUCKET['1min']]: '1 minute',
				[TOKEN_COOLDOWN_TIME_BUCKET['2min']]: '2 minutes',
				[TOKEN_COOLDOWN_TIME_BUCKET['5min']]: '5 minutes',
				[TOKEN_COOLDOWN_TIME_BUCKET['10min']]: '10 minutes',
				[TOKEN_COOLDOWN_TIME_BUCKET['15min']]: '15 minutes',
				[TOKEN_COOLDOWN_TIME_BUCKET['30min']]: '30 minutes',
				[TOKEN_COOLDOWN_TIME_BUCKET['1hr']]: '1 hour',
				[TOKEN_COOLDOWN_TIME_BUCKET['2hr']]: '2 hours',
				[TOKEN_COOLDOWN_TIME_BUCKET['3hr']]: '3 hours',
				[TOKEN_COOLDOWN_TIME_BUCKET['12hr']]: '12 hours',
				[TOKEN_COOLDOWN_TIME_BUCKET['1day']]: '1 day',
				[TOKEN_COOLDOWN_TIME_BUCKET['3day']]: '3 days',
				[TOKEN_COOLDOWN_TIME_BUCKET['7day']]: '7 days',
				[TOKEN_COOLDOWN_TIME_BUCKET['30day']]: '30 days',
			};
			return map[timeBucket] || timeBucket;
		},
	},
	methods: {
		/** Humanize large token counts for display: 10000 → "10k", 1500 → "1.5k", 1.2e6 → "1.2M". */
		formatCompactTokenCount (num: number): string {
			if (typeof num !== 'number' || !Number.isFinite(num)) return '—';
			const n = Math.round(num);
			if (n >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
			if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
			return String(n);
		},
		/** Open the cooldown menu programmatically (e.g. when Run is blocked by cooldown). */
		openMenu () {
			this.menuOpen = true;
		},
	},
});
